import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button, Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { addAccount } from '../../accountService.js';
import { ROLE_TYPES, BUSINESS_LINE_TYPES, REGION_TYPES } from '@/constants/constants.js';
import './AccountAddPage.scss';
import {
  SYSTEM_MANAGEMENT_SECTIONS,
  getSystemManagementSectionPath,
} from '@/features/systemManagement/routes/systemManagementNavigation.js';

const AccountAddPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 验证模式
  const validationSchema = Yup.object().shape({
    account: Yup.string()
      .required('账号不能为空')
      .min(3, '账号至少 3 个字符')
      .max(50, '账号最多 50 个字符')
      .matches(/^[A-Za-z0-9]+$/, '账号只能包含字母和数字'),
    password: Yup.string()
      .min(6, '密码至少 6 个字符')
      .required('密码不能为空'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], '两次输入的密码不一致')
      .required('请再次输入密码'),
    companyName: Yup.string()
      .required('公司名称不能为空')
      .max(50, '公司名称最多 50 个字符'),
    section: Yup.string().when('roleId', (roleId, schema) => {
      return Number(roleId) === 3
        ? schema.required('请选择板块')
        : schema.notRequired();
    }),
    region: Yup.string().when('roleId', (roleId, schema) => {
      return Number(roleId) === 3
        ? schema.required('请选择区域')
        : schema.notRequired();
    }),
    alias: Yup.string().when('roleId', (roleId, schema) => {
      return Number(roleId) === 3
        ? schema.required('公司别称不能为空').max(50, '公司别称最多50个字符')
        : schema.notRequired();
    }),
    gzhCode: Yup.string().when('roleId', (roleId, schema) => {
      return Number(roleId) === 3
        ? schema.required('园区编码不能为空').max(50, '园区编码最多50个字符')
        : schema.notRequired();
    }),
    chargeStandard: Yup.string().when('roleId', (roleId, schema) => {
      return Number(roleId) === 3
        ? schema.required('请选择收费标准')
        : schema.notRequired();
    }),
    roleId: Yup.number()
      .required('请选择角色')
      .oneOf([1, 2, 3], '请选择一个有效的角色'),
  });

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitting(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      // 构造发送的 payload，账号强制加前缀 fsy
      const usernameToSend = `fsy${values.account}`;

      // 构造发送的 payload：
      // - 账号字段映射为 name（并强制加 fsy 前缀）
      // - 板块映射为 businessLine，别称映射为 company_alias，区域为 region
      const payload = {
        name: usernameToSend,
        password: values.password,
        companyName: values.companyName,
        roleId: values.roleId,
      };

      if (Number(values.roleId) === 3) {
        // 客服额外字段
        if (values.section) payload.businessLine = values.section;
        if (values.region) payload.region = values.region;
        if (values.alias) payload.company_alias = values.alias;
        if (values.gzhCode) payload.gzhCode = values.gzhCode;
        if (values.chargeStandard) payload.charge_standard = values.chargeStandard;
      }

      // 调用添加账户 API
      const response = await addAccount(payload);

      if (response.status === 200 || response.status === 201) {
        setSuccessMessage('账户创建成功！即将返回账户列表...');
        resetForm();
        // 3 秒后返回账户列表
        setTimeout(() => {
          navigate(getSystemManagementSectionPath(SYSTEM_MANAGEMENT_SECTIONS.ACCOUNTS));
        }, 2000);
      } else {
        setSubmitError(response.data?.message || '账户创建失败，请稍后重试');
      }
    } catch (error) {
      console.error('添加账户失败:', error);
      if (error.response?.status === 409) {
        setSubmitError('账号名已存在，请使用其他信息重试');
      } else if (error.response?.status === 400) {
        setSubmitError(error.response.data?.message || '输入信息有误，请检查');
      } else {
        setSubmitError('添加账户失败，请稍后重试');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="add-account-page">
      <Row className="add-account-section justify-content-center">
        <Col md={6} className="add-account-col">
          <Card className="add-account-card">
            <Card.Header className="text-center">
              <h2>添加新账户</h2>
            </Card.Header>
            <Card.Body>
              {successMessage && (
                <div className="alert alert-success" role="alert">
                  {successMessage}
                </div>
              )}
              {submitError && (
                <div className="alert alert-danger" role="alert">
                  {submitError}
                </div>
              )}

              <Formik
                initialValues={{
                  account: '',
                  password: '',
                  confirmPassword: '',
                  companyName: '',
                  roleId: 3,
                  section: '',
                  region: '',
                  alias: '',
                  gzhCode: '',
                  chargeStandard: '',
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isValid, touched, errors, values }) => (
                  <Form autoComplete="off">
                    {/* hidden dummy fields to discourage browser autofill */}
                    <input type="text" name="fake-username" autoComplete="username" style={{ display: 'none' }} />
                    <input type="password" name="fake-password" autoComplete="new-password" style={{ display: 'none' }} />
                        {/* 角色选择 */}
                    <div className="form-group mb-3">
                      <label htmlFor="roleId" className="form-label">
                        角色 <span className="text-danger">*</span>
                      </label>
                      <Field name="roleId">
                        {({ field, form }) => (
                          <select
                            {...field}
                            id="roleId"
                            className={`form-control ${
                              touched.roleId && errors.roleId ? 'is-invalid' : ''
                            }`}
                            onChange={(e) => form.setFieldValue('roleId', Number(e.target.value))}
                          >
                            <option value="">请选择角色</option>
                            {Object.entries(ROLE_TYPES)
                              .filter(([label]) => label !== Object.keys(ROLE_TYPES).find(key => ROLE_TYPES[key] === ROLE_TYPES.管理员))
                              .map(([label, value]) => (
                                <option key={value} value={Number(value)}>
                                  {label}
                                </option>
                              ))}
                          </select>
                        )}
                      </Field>
                      <ErrorMessage name="roleId">
                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                      </ErrorMessage>
                    </div>

                    {/* 账号（前缀 fsy 不可删除） */}
                    <div className="form-group mb-3">
                      <label htmlFor="account" className="form-label">
                        账号 <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">fsy</span>
                        <Field name="account">
                            {({ field, form }) => (
                            <input
                              {...field}
                              id="account"
                              type="text"
                              autoComplete="off"
                              className={`form-control ${
                                touched.account && errors.account ? 'is-invalid' : ''
                              }`}
                              placeholder="请输入账号（仅支持字母和数字）"
                              onChange={(e) => {
                                // 只允许 A-Za-z0-9，移除其它字符
                                const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, '');
                                form.setFieldValue('account', sanitized);
                              }}
                              onPaste={(e) => {
                                // 处理粘贴：只保留字母和数字
                                e.preventDefault();
                                const paste = (e.clipboardData || window.clipboardData).getData('text');
                                const sanitized = paste.replace(/[^A-Za-z0-9]/g, '');
                                form.setFieldValue('account', sanitized);
                              }}
                            />
                          )}
                        </Field>
                      </div>
                      <ErrorMessage name="account">
                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                      </ErrorMessage>
                    </div>

                 

                    {/* 密码 */}
                    <div className="form-group mb-3">
                      <label htmlFor="password" className="form-label">
                        密码 <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <Field
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          name="password"
                          autoComplete="new-password"
                          className={`form-control ${
                            touched.password && errors.password ? 'is-invalid' : ''
                          }`}
                          placeholder="请输入密码（至少 6 字符，包含字母和数字）"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? '隐藏密码' : '显示密码'}
                        >
                          <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                        </button>
                      </div>
                      <ErrorMessage name="password">
                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                      </ErrorMessage>
                    </div>

                    {/* 确认密码 */}
                    <div className="form-group mb-3">
                      <label htmlFor="confirmPassword" className="form-label">
                        确认密码 <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <Field
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          name="confirmPassword"
                          autoComplete="new-password"
                          className={`form-control ${
                            touched.confirmPassword && errors.confirmPassword
                              ? 'is-invalid'
                              : ''
                          }`}
                          placeholder="请再次输入密码"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          aria-label={showConfirmPassword ? '隐藏确认密码' : '显示确认密码'}
                        >
                          <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                        </button>
                      </div>
                      <ErrorMessage name="confirmPassword">
                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                      </ErrorMessage>
                    </div>

                      {/* 公司名称（用于登录/业务用途） */}
                      <div className="form-group mb-3">
                        <label htmlFor="companyName" className="form-label">
                            {values.roleId === 3 ?  '公司名称' : '账户名称'} <span className="text-danger">*</span>
                          </label>
                        <Field
                          type="text"
                          id="companyName"
                          name="companyName"
                          className={`form-control ${
                            touched.companyName && errors.companyName
                              ? 'is-invalid'
                              : ''
                          }`}
                          placeholder="请输入公司名称"
                        />
                        <ErrorMessage name="companyName">
                          {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                        </ErrorMessage>
                      </div>

                      {/* 客服角色需要的额外字段 */}
                      {values.roleId === 3 && (
                        <>
                          <div className="form-group mb-3">
                            <label htmlFor="section" className="form-label">
                              板块 <span className="text-danger">*</span>
                            </label>
                            <Field
                              as="select"
                              id="section"
                              name="section"
                              className={`form-control ${
                                touched.section && errors.section ? 'is-invalid' : ''
                              }`}
                            >
                              <option value="">请选择板块</option>
                              {Object.entries(BUSINESS_LINE_TYPES).map(([label, value]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </Field>
                            <ErrorMessage name="section">
                              {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                            </ErrorMessage>
                          </div>

                          <div className="form-group mb-3">
                            <label htmlFor="region" className="form-label">
                              区域 <span className="text-danger">*</span>
                            </label>
                            <Field
                              as="select"
                              id="region"
                              name="region"
                              className={`form-control ${
                                touched.region && errors.region ? 'is-invalid' : ''
                              }`}
                            >
                              <option value="">请选择区域</option>
                              {Object.entries(REGION_TYPES).map(([label, value]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </Field>
                            <ErrorMessage name="region">
                              {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                            </ErrorMessage>
                          </div>

                          <div className="form-group mb-3">
                            <label htmlFor="alias" className="form-label">
                              公司别称 <span className="text-danger">*</span>
                            </label>
                            <Field
                              type="text"
                              id="alias"
                              name="alias"
                              className={`form-control ${
                                touched.alias && errors.alias ? 'is-invalid' : ''
                              }`}
                              placeholder="请输入公司别称"
                            />
                            <ErrorMessage name="alias">
                              {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                            </ErrorMessage>
                          </div>

                          <div className="form-group mb-3">
                            <label htmlFor="gzhCode" className="form-label">
                              园区编码 <span className="text-danger">*</span>
                            </label>
                            <Field
                              type="text"
                              id="gzhCode"
                              name="gzhCode"
                              className={`form-control ${
                                touched.gzhCode && errors.gzhCode ? 'is-invalid' : ''
                              }`}
                              placeholder="请输入园区编码"
                            />
                            <ErrorMessage name="gzhCode">
                              {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                            </ErrorMessage>
                          </div>

                          <div className="form-group mb-3">
                            <label htmlFor="chargeStandard" className="form-label">
                              收费标准 <span className="text-danger">*</span>
                            </label>
                            <Field
                              as="select"
                              id="chargeStandard"
                              name="chargeStandard"
                              className={`form-control ${
                                touched.chargeStandard && errors.chargeStandard ? 'is-invalid' : ''
                              }`}
                            >
                              <option value="">请选择收费标准</option>
                              {Object.entries(BUSINESS_LINE_TYPES).map(([label, value]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </Field>
                            <ErrorMessage name="chargeStandard">
                              {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                            </ErrorMessage>
                          </div>
                        </>
                      )}

                    {/* 提交和取消按钮 */}
                    <div className="d-flex gap-2 mt-4">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting || !isValid}
                        className="flex-grow-1"
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            创建中...
                          </>
                        ) : (
                          '创建账户'
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          navigate(getSystemManagementSectionPath(SYSTEM_MANAGEMENT_SECTIONS.ACCOUNTS))
                        }
                        disabled={isSubmitting}
                        className="flex-grow-1"
                      >
                        取消
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AccountAddPage;
